import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Label } from '../../../components/ui/Label';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { 
  CreditCard, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  Users,
  DollarSign,
  Calendar,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Filter,
  Search,
  Save,
  RotateCcw,
  Eye,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Zap,
  Target,
  Clock,
  FileText,
  Download,
  Upload,
  TrendingUp,
  TrendingDown,
  Star,
  Crown,
  Shield,
  Activity,
  RefreshCw,
  Copy,
  ExternalLink
} from 'lucide-react';
import { useDataContext } from '../../../contexts/DataContext';

const SubscriptionManagement = () => {
  const { getMockData, updateMockData } = useDataContext();
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isCreatingPlan, setIsCreatingPlan] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  useEffect(() => {
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = async () => {
    try {
      const data = getMockData('subscriptions');
      const plansData = getMockData('subscriptionPlans');
      const customersData = getMockData('customers');
      
      setSubscriptions(data?.subscriptions || []);
      setPlans(plansData?.plans || []);
      setCustomers(customersData?.customers || []);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    }
  };

  const handleCreatePlan = () => {
    setIsCreatingPlan(true);
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
  };

  const handleDeletePlan = (planId) => {
    if (window.confirm('Are you sure you want to delete this subscription plan?')) {
      const updatedPlans = plans.filter(plan => plan.id !== planId);
      setPlans(updatedPlans);
      updateMockData('subscriptionPlans', { plans: updatedPlans });
    }
  };

  const handleTogglePlan = (planId) => {
    const updatedPlans = plans.map(plan => 
      plan.id === planId ? { ...plan, isActive: !plan.isActive } : plan
    );
    setPlans(updatedPlans);
    updateMockData('subscriptionPlans', { plans: updatedPlans });
  };

  const handleUpgradeSubscription = (subscriptionId) => {
    alert(`Upgrading subscription ${subscriptionId} to next tier...`);
  };

  const handleDowngradeSubscription = (subscriptionId) => {
    alert(`Downgrading subscription ${subscriptionId} to previous tier...`);
  };

  const handleCancelSubscription = (subscriptionId) => {
    if (window.confirm('Are you sure you want to cancel this subscription?')) {
      const updatedSubscriptions = subscriptions.map(sub => 
        sub.id === subscriptionId ? { ...sub, status: 'cancelled' } : sub
      );
      setSubscriptions(updatedSubscriptions);
      updateMockData('subscriptions', { subscriptions: updatedSubscriptions });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'cancelled': return 'red';
      case 'expired': return 'red';
      case 'trial': return 'blue';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Pause;
      case 'cancelled': return AlertTriangle;
      case 'expired': return AlertTriangle;
      case 'trial': return Clock;
      default: return AlertTriangle;
    }
  };

  const getPlanIcon = (planType) => {
    switch (planType) {
      case 'basic': return Star;
      case 'professional': return Crown;
      case 'enterprise': return Shield;
      default: return Star;
    }
  };

  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = subscription.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.planName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || subscription.status === filterStatus;
    const matchesPlan = filterPlan === 'all' || subscription.planType === filterPlan;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  const mockSubscriptions = [
    {
      id: 1,
      customerName: "Acme Corporation",
      customerEmail: "billing@acme.com",
      planName: "Enterprise Plan",
      planType: "enterprise",
      status: "active",
      monthlyPrice: 999,
      annualPrice: 9999,
      billingCycle: "annual",
      startDate: "2024-01-01T00:00:00Z",
      nextBillingDate: "2025-01-01T00:00:00Z",
      features: ["Unlimited tickets", "Advanced analytics", "Priority support", "Custom integrations"],
      userLimit: 1000,
      currentUsers: 847,
      usagePercentage: 84.7,
      lastPayment: "2024-12-01T00:00:00Z",
      paymentMethod: "Credit Card ending in 1234",
      autoRenew: true
    },
    {
      id: 2,
      customerName: "TechStart Inc",
      customerEmail: "admin@techstart.com",
      planName: "Professional Plan",
      planType: "professional",
      status: "active",
      monthlyPrice: 299,
      annualPrice: 2999,
      billingCycle: "monthly",
      startDate: "2024-06-15T00:00:00Z",
      nextBillingDate: "2025-01-15T00:00:00Z",
      features: ["500 tickets/month", "Standard analytics", "Email support", "API access"],
      userLimit: 50,
      currentUsers: 32,
      usagePercentage: 64.0,
      lastPayment: "2024-12-15T00:00:00Z",
      paymentMethod: "Bank Transfer",
      autoRenew: true
    },
    {
      id: 3,
      customerName: "SmallBiz Solutions",
      customerEmail: "owner@smallbiz.com",
      planName: "Basic Plan",
      planType: "basic",
      status: "trial",
      monthlyPrice: 99,
      annualPrice: 999,
      billingCycle: "monthly",
      startDate: "2024-12-01T00:00:00Z",
      nextBillingDate: "2025-01-01T00:00:00Z",
      features: ["100 tickets/month", "Basic analytics", "Community support"],
      userLimit: 10,
      currentUsers: 7,
      usagePercentage: 70.0,
      lastPayment: null,
      paymentMethod: "Trial - No payment required",
      autoRenew: false
    },
    {
      id: 4,
      customerName: "Global Enterprises",
      customerEmail: "it@globalent.com",
      planName: "Enterprise Plan",
      planType: "enterprise",
      status: "cancelled",
      monthlyPrice: 999,
      annualPrice: 9999,
      billingCycle: "annual",
      startDate: "2023-01-01T00:00:00Z",
      nextBillingDate: "2024-01-01T00:00:00Z",
      features: ["Unlimited tickets", "Advanced analytics", "Priority support", "Custom integrations"],
      userLimit: 1000,
      currentUsers: 0,
      usagePercentage: 0,
      lastPayment: "2023-12-01T00:00:00Z",
      paymentMethod: "Credit Card ending in 5678",
      autoRenew: false
    },
    {
      id: 5,
      customerName: "StartupCo",
      customerEmail: "founder@startupco.com",
      planName: "Professional Plan",
      planType: "professional",
      status: "active",
      monthlyPrice: 299,
      annualPrice: 2999,
      billingCycle: "annual",
      startDate: "2024-03-01T00:00:00Z",
      nextBillingDate: "2025-03-01T00:00:00Z",
      features: ["500 tickets/month", "Standard analytics", "Email support", "API access"],
      userLimit: 50,
      currentUsers: 45,
      usagePercentage: 90.0,
      lastPayment: "2024-03-01T00:00:00Z",
      paymentMethod: "Credit Card ending in 9012",
      autoRenew: true
    }
  ];

  const mockPlans = [
    {
      id: 1,
      name: "Basic Plan",
      type: "basic",
      description: "Perfect for small teams getting started",
      monthlyPrice: 99,
      annualPrice: 999,
      features: [
        "100 tickets per month",
        "Basic analytics dashboard",
        "Community support",
        "Email notifications",
        "Mobile app access"
      ],
      userLimit: 10,
      isActive: true,
      isPopular: false,
      createdAt: "2024-01-01T00:00:00Z"
    },
    {
      id: 2,
      name: "Professional Plan",
      type: "professional",
      description: "Ideal for growing businesses",
      monthlyPrice: 299,
      annualPrice: 2999,
      features: [
        "500 tickets per month",
        "Advanced analytics",
        "Priority email support",
        "API access",
        "Custom branding",
        "SLA management"
      ],
      userLimit: 50,
      isActive: true,
      isPopular: true,
      createdAt: "2024-01-01T00:00:00Z"
    },
    {
      id: 3,
      name: "Enterprise Plan",
      type: "enterprise",
      description: "Complete solution for large organizations",
      monthlyPrice: 999,
      annualPrice: 9999,
      features: [
        "Unlimited tickets",
        "Advanced analytics & reporting",
        "24/7 priority support",
        "Full API access",
        "Custom integrations",
        "Advanced security features",
        "Dedicated account manager"
      ],
      userLimit: 1000,
      isActive: true,
      isPopular: false,
      createdAt: "2024-01-01T00:00:00Z"
    }
  ];

  const subscriptionMetrics = [
    {
      title: 'Total Subscriptions',
      value: subscriptions.length,
      icon: CreditCard,
      color: 'blue',
      trend: '+3',
      trendDirection: 'up'
    },
    {
      title: 'Active Subscriptions',
      value: subscriptions.filter(s => s.status === 'active').length,
      icon: CheckCircle,
      color: 'green',
      trend: '+2',
      trendDirection: 'up'
    },
    {
      title: 'Monthly Recurring Revenue',
      value: '$12,450',
      icon: DollarSign,
      color: 'green',
      trend: '+8.5%',
      trendDirection: 'up'
    },
    {
      title: 'Churn Rate',
      value: '2.1%',
      icon: TrendingDown,
      color: 'green',
      trend: '-0.3%',
      trendDirection: 'down'
    }
  ];

  return (
    <div className="subscription-management p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Subscription Management</h1>
            <p className="text-gray-600">Manage subscription plans and customer subscriptions</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button onClick={handleCreatePlan}>
              <Plus className="h-4 w-4 mr-2" />
              Create Plan
            </Button>
            <Button variant="outline">
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {subscriptionMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">{metric.title}</CardTitle>
                <Icon className={`h-4 w-4 text-${metric.color}-500`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                <div className={`flex items-center text-xs ${
                  metric.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  {metric.trend} from last month
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Subscription Plans */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Star className="h-5 w-5 mr-2" />
            Subscription Plans
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockPlans.map((plan) => {
              const PlanIcon = getPlanIcon(plan.type);
              return (
                <div key={plan.id} className={`p-6 border rounded-lg ${
                  plan.isPopular ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <PlanIcon className="h-6 w-6 text-blue-600" />
                      <h3 className="text-lg font-semibold">{plan.name}</h3>
                    </div>
                    {plan.isPopular && (
                      <Badge variant="default">Most Popular</Badge>
                    )}
                  </div>
                  
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  
                  <div className="mb-4">
                    <div className="text-3xl font-bold text-gray-900">
                      ${plan.monthlyPrice}
                      <span className="text-lg text-gray-500">/month</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      ${plan.annualPrice}/year (save ${(plan.monthlyPrice * 12) - plan.annualPrice})
                    </div>
                  </div>

                  <div className="mb-4">
                    <h4 className="font-medium text-gray-700 mb-2">Features:</h4>
                    <ul className="space-y-1">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-3 w-3 mr-2 text-green-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      Up to {plan.userLimit} users
                    </span>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEditPlan(plan)}>
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleTogglePlan(plan.id)}>
                        {plan.isActive ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Disable
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Enable
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search subscriptions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPlan} onValueChange={setFilterPlan}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by plan" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="basic">Basic</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="h-5 w-5 mr-2" />
            Customer Subscriptions ({mockSubscriptions.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {mockSubscriptions.map((subscription) => {
              const StatusIcon = getStatusIcon(subscription.status);
              const PlanIcon = getPlanIcon(subscription.planType);
              return (
                <div key={subscription.id} className="p-6 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{subscription.customerName}</h3>
                        <Badge variant={subscription.status === 'active' ? 'default' : 'secondary'}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {subscription.status}
                        </Badge>
                        <Badge variant="outline" className="flex items-center">
                          <PlanIcon className="h-3 w-3 mr-1" />
                          {subscription.planName}
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-3">{subscription.customerEmail}</p>
                      
                      {/* Pricing and Billing */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Pricing & Billing:</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>${subscription.billingCycle === 'annual' ? subscription.annualPrice : subscription.monthlyPrice} per {subscription.billingCycle}</span>
                          <span>Next billing: {new Date(subscription.nextBillingDate).toLocaleDateString()}</span>
                          <Badge variant="outline" className="text-xs">
                            {subscription.autoRenew ? 'Auto-renew' : 'Manual'}
                          </Badge>
                        </div>
                      </div>

                      {/* Usage */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Usage:</h4>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <span>{subscription.currentUsers} / {subscription.userLimit} users</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full" 
                              style={{ width: `${subscription.usagePercentage}%` }}
                            ></div>
                          </div>
                          <span>{subscription.usagePercentage}%</span>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Features:</h4>
                        <div className="flex flex-wrap gap-2">
                          {subscription.features.map((feature, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {feature}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Payment Info */}
                      <div className="flex items-center space-x-6 text-sm text-gray-600">
                        <div className="flex items-center">
                          <CreditCard className="h-4 w-4 mr-1" />
                          <span>{subscription.paymentMethod}</span>
                        </div>
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>Started: {new Date(subscription.startDate).toLocaleDateString()}</span>
                        </div>
                        {subscription.lastPayment && (
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            <span>Last payment: {new Date(subscription.lastPayment).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      {subscription.status === 'active' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpgradeSubscription(subscription.id)}
                          >
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Upgrade
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDowngradeSubscription(subscription.id)}
                          >
                            <TrendingDown className="h-3 w-3 mr-1" />
                            Downgrade
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditPlan(subscription)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      {subscription.status === 'active' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCancelSubscription(subscription.id)}
                        >
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Zap className="h-5 w-5 mr-2" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Button className="h-20 flex flex-col items-center justify-center space-y-2">
              <Plus className="h-6 w-6" />
              <span>Create Plan</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <BarChart3 className="h-6 w-6" />
              <span>Revenue Analytics</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <Download className="h-6 w-6" />
              <span>Export Data</span>
            </Button>
            <Button variant="outline" className="h-20 flex flex-col items-center justify-center space-y-2">
              <Settings className="h-6 w-6" />
              <span>Billing Settings</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManagement;
